import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { allTables, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionErgebniszeile: Lesson = {
  id: "ergebniszeile",
  title: "Ergebniszeile, Filterschaltflächen und Formatvorlage",
  summary: "Was eine Tabelle mitbringt, ohne dass man es bauen muss.",
  durationMinutes: 10,
  learningGoals: [
    "Die Ergebniszeile einschalten und ihre Funktion wechseln",
    "Erklären, warum TEILERGEBNIS dort steht und nicht SUMME",
    "Tabellenformatvorlagen bewusst wählen",
  ],
  steps: [
    {
      id: "ergebniszeile-einschalten",
      title: "Die Ergebniszeile",
      explanation: [
        "Unter „Tabellenentwurf“ gibt es ein Häkchen „Ergebniszeile“. Es hängt unter " +
          "die Tabelle eine zusätzliche Zeile, die für die letzte Spalte gleich eine " +
          "Summe bildet. Klickst du in eine Zelle dieser Zeile, erscheint ein " +
          "Auswahlpfeil – dort lässt sich für jede Spalte einzeln wählen, ob dort " +
          "Summe, Mittelwert, Anzahl, Minimum oder Maximum stehen soll.",
        "Was Excel dabei einsetzt, ist bemerkenswert: nicht SUMME, sondern " +
          "TEILERGEBNIS. Der Unterschied ist der Grund, warum die Ergebniszeile mehr " +
          "kann als eine selbstgebaute Summe – TEILERGEBNIS zählt nur die Zeilen mit, " +
          "die gerade sichtbar sind. Filterst du die Tabelle auf eine Region, zeigt die " +
          "Ergebniszeile sofort die Summe dieser Region.",
        "Eine gewöhnliche SUMME darunter würde weiterhin alles addieren, auch die " +
          "ausgeblendeten Zeilen. Beide Verhaltensweisen haben ihren Platz: Für einen " +
          "Gesamtwert ist SUMME richtig, für eine mitlaufende Auswertung beim Filtern " +
          "TEILERGEBNIS. Die Ergebniszeile nimmt einem die Entscheidung ab, indem sie " +
          "das Naheliegendere wählt.",
        "Die Ergebniszeile bleibt beim Sortieren und Filtern unten stehen und wird " +
          "nicht mitsortiert – anders als eine Summe, die man selbst unter die Daten " +
          "geschrieben hat und die beim Sortieren gern mitten in den Zahlen landet.",
      ],
      keyPoints: [
        "Tabellenentwurf → Ergebniszeile. Die Funktion je Spalte über den Auswahlpfeil.",
        "Dort steht TEILERGEBNIS: Es zählt nur sichtbare Zeilen.",
        "Die Ergebniszeile wird beim Sortieren nicht mitsortiert.",
      ],
      task: {
        instruction: "Schalte für deine Tabelle die Ergebniszeile ein.",
        hint: "Cursor in die Tabelle, dann Tabellenentwurf → Häkchen bei „Ergebniszeile“.",
        check: async () => {
          const mappe = await readWorkbook();
          const tabellen = allTables(mappe);

          if (tabellen.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch keine Tabelle.",
              "Wandle deine Liste zuerst mit Strg + T in eine Tabelle um.",
            );
          }

          const lesbar = tabellen.filter((tabelle) => tabelle.showTotals !== null);
          if (lesbar.length === 0) {
            return nichtPruefbar(
              "Die Ergebniszeile einer Tabelle",
              "1.1",
              "Sieh nach, ob unter deiner Tabelle eine zusätzliche Zeile mit einem Ergebnis steht.",
            );
          }

          const mitErgebnis = lesbar.filter((tabelle) => tabelle.showTotals === true);
          if (mitErgebnis.length === 0) {
            return fail(
              `Keine deiner ${lesbar.length} ${lesbar.length === 1 ? "Tabelle" : "Tabellen"} zeigt eine Ergebniszeile.`,
              "Cursor in die Tabelle, dann unter „Tabellenentwurf“ das Häkchen bei „Ergebniszeile“ setzen.",
            );
          }

          return pass(
            `„${mitErgebnis[0].name}“ hat jetzt eine Ergebniszeile. Filtere die Tabelle einmal – ` +
              "das Ergebnis rechnet sich mit, weil dort TEILERGEBNIS steht und nicht SUMME.",
          );
        },
      },
    },
    {
      id: "filterschaltflaechen",
      title: "Filterschaltflächen",
      explanation: [
        "Jede Tabelle bringt in der Kopfzeile Filterschaltflächen mit – die kleinen " +
          "Pfeile, über die sich sortieren und filtern lässt. Sie sind von Anfang an da " +
          "und brauchen keinen eigenen Handgriff; wie man sie benutzt, ist Thema des " +
          "nächsten Moduls.",
        "Ausschalten lassen sie sich unter „Tabellenentwurf“ mit dem Häkchen " +
          "„Filterschaltfläche“. Das ist nur in einem Fall sinnvoll: wenn die Tabelle " +
          "gedruckt oder als Bericht weitergegeben wird und die Pfeile stören. Zum " +
          "Arbeiten gehören sie eingeschaltet.",
        "Ein Detail, das später wichtig wird: Auch wenn du filterst, bleiben die " +
          "ausgeblendeten Zeilen Teil der Tabelle. Eine SUMME über die Spalte addiert " +
          "sie weiterhin mit. Nur TEILERGEBNIS – und damit die Ergebniszeile – " +
          "beschränkt sich auf das Sichtbare.",
      ],
      keyPoints: [
        "Filterschaltflächen sind in jeder Tabelle von Anfang an vorhanden.",
        "Tabellenentwurf → Filterschaltfläche schaltet sie aus, etwa fürs Drucken.",
        "Gefilterte Zeilen bleiben Teil der Tabelle und werden von SUMME mitgezählt.",
      ],
      task: {
        instruction:
          "Stelle sicher, dass die Filterschaltflächen in der Kopfzeile deiner Tabelle " +
          "eingeschaltet sind.",
        hint: "Cursor in die Tabelle, dann Tabellenentwurf → Häkchen bei „Filterschaltfläche“.",
        check: async () => {
          const mappe = await readWorkbook();
          const tabellen = allTables(mappe);

          if (tabellen.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch keine Tabelle.",
              "Wandle deine Liste zuerst mit Strg + T in eine Tabelle um.",
            );
          }

          if (!mappe.features.tableDetails) {
            return nichtPruefbar(
              "Die Filterschaltflächen einer Tabelle",
              "1.3",
              "Sieh in der Kopfzeile deiner Tabelle nach, ob dort kleine Pfeile zum Aufklappen stehen.",
              true,
            );
          }

          const ohne = tabellen.filter((tabelle) => tabelle.showFilterButton === false);
          if (ohne.length > 0) {
            return fail(
              `In der Tabelle „${ohne[0].name}“ sind die Filterschaltflächen ausgeschaltet.`,
              "Cursor in die Tabelle, dann unter „Tabellenentwurf“ das Häkchen bei „Filterschaltfläche“ setzen.",
            );
          }

          return pass(
            `Die Kopfzeile von „${tabellen[0].name}“ zeigt ihre Filterschaltflächen – ` +
              "Sortieren und Filtern sind damit einen Klick entfernt.",
          );
        },
      },
    },
    {
      id: "formatvorlage",
      title: "Tabellenformatvorlagen",
      explanation: [
        "Beim Umwandeln bekommt jede Tabelle eine Formatvorlage: Kopfzeile farbig " +
          "hinterlegt, Datenzeilen abwechselnd getönt. Diese Zebrastreifen sind kein " +
          "Selbstzweck – sie halten das Auge beim Lesen einer langen Zeile in der Spur, " +
          "und zwar deutlich besser als Rahmenlinien.",
        "Unter „Tabellenentwurf“ steht ein Katalog mit Varianten. Die Häkchen daneben " +
          "steuern die Bestandteile einzeln: Kopfzeile, Ergebniszeile, verbundene " +
          "Zeilen, verbundene Spalten, erste und letzte Spalte hervorheben. Verbundene " +
          "Zeilen und Spalten gleichzeitig einzuschalten, ergibt ein Schachbrett und " +
          "hilft niemandem – eins von beidem reicht.",
        "Der entscheidende Vorteil gegenüber von Hand gesetzten Farben: Die Vorlage " +
          "gilt für die Tabelle, nicht für bestimmte Zellen. Kommen unten Zeilen dazu, " +
          "sind sie automatisch richtig eingefärbt. Wer die Streifen von Hand setzt, " +
          "darf beim Sortieren zusehen, wie sie durcheinandergeraten – die Farbe klebt " +
          "dann an der Zelle und wandert mit den Daten mit.",
      ],
      keyPoints: [
        "Zebrastreifen führen das Auge besser durch eine Zeile als Rahmenlinien.",
        "Tabellenentwurf → Katalog der Formatvorlagen, daneben die Häkchen für die Teile.",
        "Verbundene Zeilen oder verbundene Spalten – nie beides gleichzeitig.",
      ],
      task: {
        instruction:
          "Wähle für deine Tabelle eine Formatvorlage mit verbundenen Zeilen, sodass " +
          "die Datenzeilen abwechselnd getönt sind.",
        hint: "Cursor in die Tabelle, dann Tabellenentwurf → eine Vorlage aus dem Katalog und das Häkchen „Verbundene Zeilen“.",
        check: async () => {
          const mappe = await readWorkbook();
          const tabellen = allTables(mappe);

          if (tabellen.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch keine Tabelle.",
              "Wandle deine Liste zuerst mit Strg + T in eine Tabelle um.",
            );
          }

          if (!mappe.features.tableDetails) {
            return nichtPruefbar(
              "Die verbundenen Zeilen einer Tabelle",
              "1.3",
              "Sieh nach, ob die Datenzeilen deiner Tabelle abwechselnd getönt sind.",
              true,
            );
          }

          const gestreift = tabellen.filter((tabelle) => tabelle.showBandedRows === true);
          if (gestreift.length === 0) {
            return fail(
              `Keine deiner ${tabellen.length} ${tabellen.length === 1 ? "Tabelle" : "Tabellen"} zeigt verbundene Zeilen.`,
              "Cursor in die Tabelle, dann unter „Tabellenentwurf“ das Häkchen bei „Verbundene Zeilen“ setzen.",
            );
          }

          const erste = gestreift[0];
          return pass(
            `„${erste.name}“ trägt die Formatvorlage ${erste.style ? `„${erste.style}“` : "einer Tabelle"} ` +
              "mit verbundenen Zeilen. Neue Zeilen fügen sich automatisch in das Muster ein.",
          );
        },
      },
    },
  ],
};
